
import React, { useState, useEffect, createContext, useContext } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SlideMenu from './components/SlideMenu';
import HomePage from './pages/HomePage';
import BrandPage from './pages/BrandPage';
import GalleryPage from './pages/GalleryPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import FAQPage from './pages/FAQPage';
import FloatingBookingButton from './components/UI/FloatingBookingButton';
import BusinessCard from './components/UI/BusinessCard';
import BookingModal from './components/UI/BookingModal';
import { siteConfig } from './config/site';

type Language = 'en' | 'es';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: any;
  openBooking: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function HashScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [pathname, hash]);
  return null;
}

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [lang, setLang] = useState<Language>(() => {
    return navigator.language.startsWith('es') ? 'es' : 'en';
  });

  const t = siteConfig.translations[lang];

  const openBooking = () => setIsBookingOpen(true);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, openBooking }}>
      <Router>
        <ScrollToTop />
        <HashScroll />
        <div className="relative min-h-screen bg-primary">
          <Header onOpenMenu={() => setIsMenuOpen(true)} />
          <SlideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
          
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/brand" element={<BrandPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/faq" element={<FAQPage />} />
            </Routes>
          </main>

          <Footer />
          <BusinessCard />
          <FloatingBookingButton />
          <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
        </div>
      </Router>
    </LanguageContext.Provider>
  );
};

export default App;
